import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface OrderNotification {
  id: string;
  total: number;
  created_at: string;
  customer_name?: string;
}

export function useOrderNotifications() {
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [latestOrder, setLatestOrder] = useState<OrderNotification | null>(null);

  useEffect(() => {
    // Subscribe to new orders
    const channel = supabase
      .channel('order-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
        },
        (payload) => {
          const newOrder = payload.new as OrderNotification;
          
          // Update state
          setNewOrderCount((prev) => prev + 1);
          setLatestOrder(newOrder);

          // Show toast notification
          const customerName = newOrder.customer_name || 'A customer';
          const amount = new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
          }).format(newOrder.total);

          toast.success(`🎉 New Order Received!`, {
            description: `${customerName} placed an order for ${amount}`,
            duration: 10000,
            action: {
              label: 'View',
              onClick: () => {
                window.location.href = '/admin/orders';
              },
            },
          });

          // Play notification sound (optional)
          playNotificationSound();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const clearNotifications = () => {
    setNewOrderCount(0);
    setLatestOrder(null);
  };

  return {
    newOrderCount,
    latestOrder,
    clearNotifications,
  };
}

// Optional: Play a notification sound
function playNotificationSound() {
  try {
    // Create a simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (error) {
    // Silently fail if audio is not supported
    console.debug('Audio notification not available:', error);
  }
}
